// Shared install state for the /app PWA: used by the install pop-up
// (PWAInstallPrompt) and the "Install app" item in the sidebar.
import { useEffect, useState } from "react";

const SNOOZE_KEY = "pwa-install-snooze"; // { count, until }
const FREE_DISMISSALS = 3;               // dismissals that only hide it until the next load
const SNOOZE_DAYS = [3, 7, 14];          // then each dismissal waits longer, capped at the last value

let deferredPrompt = null;
let initialized = false;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const isIOS = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

function readSnooze() {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY)) || { count: 0, until: 0 };
  } catch {
    return { count: 0, until: 0 };
  }
}

function clearSnooze() {
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {}
}

export function isSnoozed() {
  return Date.now() < (readSnooze().until || 0);
}

export function recordDismiss() {
  const { count } = readSnooze();
  const next = count + 1;
  let until = 0;
  if (next > FREE_DISMISSALS) {
    const days = SNOOZE_DAYS[Math.min(next - FREE_DISMISSALS - 1, SNOOZE_DAYS.length - 1)];
    until = Date.now() + days * 24 * 60 * 60 * 1000;
  }
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify({ count: next, until }));
  } catch {}
}

function init() {
  if (initialized) return;
  initialized = true;
  try {
    localStorage.removeItem("pwa-install-dismissed"); // old 7-day key
  } catch {}
  if (isStandalone()) clearSnooze();

  // index.html captures the event before React mounts
  if (window.__pwaInstallPrompt) {
    deferredPrompt = window.__pwaInstallPrompt;
    window.__pwaInstallPrompt = null;
  }
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.__pwaInstallPrompt = null;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    clearSnooze();
    notify();
  });
}

export function canInstall() {
  if (isStandalone()) return false;
  return isIOS() || !!deferredPrompt;
}

// Opens the browser's install dialog; returns true if the user accepted
export async function promptInstall() {
  if (!deferredPrompt) return false;
  const prompt = deferredPrompt;
  deferredPrompt = null; // a prompt event can only be used once
  prompt.prompt();
  const result = await prompt.userChoice;
  if (result.outcome === "accepted") clearSnooze();
  notify();
  return result.outcome === "accepted";
}

// Ask the pop-up to open (e.g. from the sidebar on iOS), ignoring the snooze
const openListeners = new Set();
export function requestInstallPopup() {
  openListeners.forEach((fn) => fn());
}
export function onInstallPopupRequest(fn) {
  openListeners.add(fn);
  return () => openListeners.delete(fn);
}

export function usePwaInstall() {
  init();
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return { canInstall: canInstall(), isIOS: isIOS() };
}
